/*
 * One pill component for every status badge in the app. Previously each list
 * page hand-rolled its own { color, bg } lookup table with slightly different
 * hues; this centralises them so "approved" is the same green everywhere.
 *
 * Callers pass a `tone` (the visual colour) and a `label`. The helpers below
 * map the app's domain statuses (job / quote / project / inventory condition)
 * to a tone so pages don't repeat the mapping.
 */

export type Tone = 'green' | 'amber' | 'red' | 'blue' | 'purple' | 'gray';

const toneStyles: Record<Tone, { color: string; bg: string }> = {
  green:  { color: 'var(--green)',  bg: 'var(--green-bg)' },
  amber:  { color: 'var(--amber)',  bg: 'var(--amber-bg)' },
  red:    { color: 'var(--red)',    bg: 'var(--red-bg)' },
  blue:   { color: 'var(--blue)',   bg: 'var(--blue-bg)' },
  purple: { color: 'var(--purple)', bg: 'var(--purple-bg)' },
  gray:   { color: 'var(--gray)',   bg: 'var(--gray-bg)' },
};

interface StatusPillProps {
  label: string;
  tone: Tone;
  /** Prefix rendered before the label, e.g. "Quote: ". Not capitalised. */
  prefix?: string;
  style?: React.CSSProperties;
}

export function StatusPill({ label, tone, prefix, style }: StatusPillProps) {
  const t = toneStyles[tone];
  return (
    <span
      style={{
        display: 'inline-block',
        color: t.color,
        background: t.bg,
        fontWeight: 600,
        textTransform: 'capitalize',
        padding: '2px 10px',
        borderRadius: 10,
        fontSize: 12,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {prefix}{label}
    </span>
  );
}

// ── Domain → tone mappers ────────────────────────────────────────────────────

export function jobStatusTone(status: string): Tone {
  switch (status) {
    case 'confirmed': return 'green';
    case 'completed': return 'blue';
    case 'reviewed':  return 'purple';
    case 'cancelled': return 'gray';
    default:          return 'gray'; // draft
  }
}

export function quoteStatusTone(status: string): Tone {
  switch (status) {
    case 'approved': return 'green';
    case 'sent':     return 'amber';
    case 'declined': return 'red';
    default:         return 'gray'; // draft
  }
}

export function projectStatusTone(status: string): Tone {
  switch (status) {
    case 'active':    return 'green';
    case 'completed': return 'blue';
    case 'cancelled': return 'gray';
    default:          return 'gray';
  }
}

export function subHireStatusTone(status: string): Tone {
  switch (status) {
    case 'confirmed': return 'green';
    case 'sent':      return 'blue';
    case 'received':  return 'purple';
    default:          return 'gray'; // draft
  }
}

export function conditionTone(condition: string): Tone {
  switch (condition) {
    case 'available':  return 'green';
    case 'in_service': return 'amber';
    case 'damaged':    return 'red';
    case 'lost':       return 'red';
    default:           return 'gray'; // retired
  }
}
